import { HttpErrorResponse } from '@angular/common/http';
import { computed, effect, inject, Injectable, untracked } from '@angular/core';
import { ApiClientService } from '../core/api/api-client.service';
import { SignalREvents } from '../core/signalr/signalr.constants';
import { SignalRService } from '../core/signalr/signalr.service';
import {
  ChatConversationModel,
  ChatConversationType,
  ChatMessageModel,
  ChatMessageReaderModel,
  ChatMessageType,
  ChatReplyPreviewModel,
  ChatReactionUpdateModel,
  ChatReadModel,
  DeleteChatMessageResponse,
  GetAdminChatStatusResponse,
  GetChatConversationsResponse,
  GetChatMessageReadersResponse,
  GetChatMessagesResponse,
  GetChatPeopleResponse,
  GetChatUnreadFacilitiesResponse,
  GetPresignedUriRequest,
  PresignedUri,
  StoreChatConversationNameRequest,
  StoreChatConversationNameResponse,
  StoreChatConversationPhotoRequest,
  StoreChatConversationPhotoResponse,
  StoreChatConversationRequest,
  StoreChatConversationResponse,
  StoreChatLastReadRequest,
  StoreChatLastReadResponse,
  StoreChatMembersRequest,
  StoreChatMembersResponse,
  StoreChatMessageReactionRequest,
  StoreChatMessageReactionResponse,
  StoreChatMessageRequest,
  StoreChatMessageResponse,
  StoreChatMutedRequest,
  StoreChatMutedResponse,
  UriType,
} from '../shared/models/chat.model';
import { MentionableText } from '../shared/models/mention.model';
import { ApiResponse } from '../shared/models/types';
import { ChatHelper } from '../shared/utils/chat-helper';
import { isImageFile, PreparedPhoto, preparePhoto } from '../shared/utils/chat-photo.utils';
import { MentionHelper } from '../shared/utils/mention-helper';
import { AppStore } from '../stores/app.store';
import { AuthStore } from '../stores/auth.store';
import { ChatConversationStore } from '../stores/chat-conversation.store';
import { ChatDockStore, ChatWindowMode } from '../stores/chat-dock.store';
import { UiStore } from '../stores/ui.store';

export type { ChatWindowMode } from '../stores/chat-dock.store';

export type ChatSendResult = 'sent' | 'failed' | 'stale';

export type ChatMembersResult = 'stored' | 'deleted' | 'failed';

const MESSAGE_PAGE_SIZE = 50;
const SEARCH_PAGE_SIZE = 25;
const UNREAD_DEBOUNCE_MS = 1000;
const READ_ONLY_REFRESH_MS = 10000;
const ALL_CHATS_REFRESH_MS = 30000;
const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';
const PHOTO_DOWNLOAD_ERROR_MESSAGE = "Couldn't download the photo.";
const UNREAD_URL = 'admin/chat/unread';
const SAS_URL = 'media/sas-url';

interface UserScope {
  userId: Guid | null;
}

interface RequestScope extends UserScope {
  facilityId: Guid;
}

@Injectable({ providedIn: 'root' })
export class ChatManagerService {
  private readonly apiClient = inject(ApiClientService);
  private readonly appStore = inject(AppStore);
  private readonly authStore = inject(AuthStore);
  private readonly uiStore = inject(UiStore);
  private readonly signalR = inject(SignalRService);
  private readonly chatConversationStore = inject(ChatConversationStore);
  private readonly chatDockStore = inject(ChatDockStore);

  private unreadDebounceHandle: ReturnType<typeof setTimeout> | null = null;
  private allChatsRefreshHandle: ReturnType<typeof setInterval> | null = null;
  private readonly readOnlyRefreshHandles = new Map<Guid, ReturnType<typeof setInterval>>();
  private readonly postQueues = new Map<Guid, Promise<ChatSendResult>>();
  private lastObservedFacilityId: Guid | null = null;
  private lastObservedUserId: Guid | null = null;
  private wasSignalRConnected = false;
  private hasSignalRConnected = false;

  readonly status = this.chatConversationStore.status;
  readonly conversations = this.chatConversationStore.conversations;
  readonly peopleByUserId = this.chatConversationStore.peopleByUserId;
  readonly unreadFacilityIds = this.chatConversationStore.unreadFacilityIds;
  readonly scrollToMessageRequest = this.chatConversationStore.scrollToMessageRequest;
  readonly isFlyoutOpen = this.chatDockStore.isFlyoutOpen;
  readonly windows = this.chatDockStore.windows;

  readonly hasUnreadAnywhere = computed(() => {
    const manageableFacilityIds = this.chatConversationStore.manageableFacilityIds();
    const hasUnreadFacility = this.chatConversationStore
      .unreadFacilityIds()
      .some((facilityId) => manageableFacilityIds.includes(facilityId));

    if (hasUnreadFacility) {
      return true;
    }

    const currentUserId = this.authStore.user()?.id;

    if (!currentUserId) {
      return false;
    }

    const hasUnreadConversation = this.chatConversationStore
      .conversations()
      .some((conversation) => ChatHelper.hasUnread(conversation, currentUserId));

    return hasUnreadConversation;
  });

  constructor() {
    effect(() => {
      const facilityId = this.appStore.selectedFacilityId();
      const userId = this.authStore.user()?.id || null;

      untracked(() => this.handleSessionChange(facilityId, userId));
    });

    effect(() => {
      const isConnected = this.signalR.isConnected();

      untracked(() => this.handleConnectionChange(isConnected));
    });
  }

  messagesFor(conversationId: Guid) {
    return this.chatConversationStore.messagesFor(conversationId);
  }

  hasOlderMessages(conversationId: Guid): boolean {
    return this.chatConversationStore.hasOlderMessages(conversationId);
  }

  conversationById(conversationId: Guid): ChatConversationModel | undefined {
    return this.chatConversationStore.conversationById(conversationId);
  }

  draftFor(conversationId: Guid): MentionableText | undefined {
    return this.chatConversationStore.draftFor(conversationId);
  }

  setManageableFacilityIds(facilityIds: Guid[]): void {
    this.chatConversationStore.setManageableFacilityIds(facilityIds);
  }

  saveDraft(conversationId: Guid, draft: MentionableText): void {
    this.chatConversationStore.setDraft(conversationId, draft);
  }

  requestScrollToMessage(conversationId: Guid, messageId: Guid): void {
    this.chatConversationStore.requestScrollToMessage(conversationId, messageId);
  }

  clearScrollToMessageRequest(): void {
    this.chatConversationStore.clearScrollToMessageRequest();
  }

  toggleFlyout(): void {
    this.chatDockStore.toggleFlyout();
  }

  openWindow(conversationId: Guid): void {
    this.chatDockStore.openWindow(conversationId);
  }

  setWindowMode(conversationId: Guid, mode: ChatWindowMode): void {
    this.chatDockStore.setWindowMode(conversationId, mode);
  }

  closeWindow(conversationId: Guid): void {
    this.chatDockStore.closeWindow(conversationId);
  }

  async loadForFacility(): Promise<void> {
    const facilityId = this.appStore.selectedFacilityId();

    if (!facilityId) {
      return;
    }

    const scope = this.captureScope(facilityId);

    try {
      const status = await this.apiClient.get<GetAdminChatStatusResponse>(`${this.baseUrl(facilityId)}/status`);

      if (!this.acceptResponse(scope, status)) {
        return;
      }

      this.chatConversationStore.setStatus(status);

      if (!status.isEnabled || this.isStale(scope)) {
        return;
      }

      await Promise.all([this.loadPeople(), this.loadConversations()]);

      if (this.isStale(scope)) {
        return;
      }

      this.chatConversationStore.setLoadedFacilityId(facilityId);

      await this.loadUnreadFacilities();
    } catch (error) {
      this.showError(error);
    }
  }

  async loadConversations(): Promise<void> {
    await this.fetchConversations(false);
  }

  private async refreshConversations(): Promise<void> {
    await this.fetchConversations(true);
  }

  private async fetchConversations(isQuiet: boolean): Promise<void> {
    const facilityId = this.appStore.selectedFacilityId();

    if (!facilityId || !this.isChatEnabled()) {
      return;
    }

    const scope = this.captureScope(facilityId);

    try {
      const response = await this.apiClient.get<GetChatConversationsResponse>(
        `${this.baseUrl(facilityId)}/conversations`
      );

      if (!this.acceptResponse(scope, response, isQuiet)) {
        return;
      }

      const conversationIds = response.conversations.map((conversation) => conversation.id);

      this.chatConversationStore.setConversations(response.conversations);
      this.chatDockStore.retainWindows(conversationIds);
    } catch (error) {
      this.showErrorUnlessQuiet(error, isQuiet);
    }
  }

  async loadNewestMessages(conversationId: Guid): Promise<void> {
    await this.fetchNewestMessages(conversationId, false);
  }

  private async refreshNewestMessages(conversationId: Guid): Promise<void> {
    await this.fetchNewestMessages(conversationId, true);
  }

  private async fetchNewestMessages(conversationId: Guid, isRefresh: boolean): Promise<void> {
    const facilityId = this.appStore.selectedFacilityId();

    if (!facilityId || !this.isChatEnabled()) {
      return;
    }

    const scope = this.captureScope(facilityId);

    try {
      const response = await this.apiClient.get<GetChatMessagesResponse>(
        `${this.baseUrl(facilityId)}/conversations/${conversationId}/messages`,
        { take: MESSAGE_PAGE_SIZE }
      );

      if (!this.acceptResponse(scope, response, isRefresh)) {
        return;
      }

      const hasLoadedMessages = this.chatConversationStore.messagesFor(conversationId)().length > 0;

      if (isRefresh && hasLoadedMessages) {
        response.chatMessages.forEach((message) => this.chatConversationStore.upsertMessage(conversationId, message));

        return;
      }

      this.chatConversationStore.setMessages(conversationId, response.chatMessages, response.hasMore);
    } catch (error) {
      this.showErrorUnlessQuiet(error, isRefresh);
    }
  }

  async loadOlderMessages(conversationId: Guid): Promise<void> {
    const facilityId = this.appStore.selectedFacilityId();
    const loadedMessages = this.chatConversationStore.messagesFor(conversationId)();
    const oldestStored = loadedMessages.find((message) => !message.isPending);

    if (!facilityId || !oldestStored) {
      return;
    }

    const scope = this.captureScope(facilityId);

    try {
      const response = await this.apiClient.get<GetChatMessagesResponse>(
        `${this.baseUrl(facilityId)}/conversations/${conversationId}/messages`,
        { take: MESSAGE_PAGE_SIZE, beforeMessageId: oldestStored.id }
      );

      if (!this.acceptResponse(scope, response)) {
        return;
      }

      this.chatConversationStore.prependOlderMessages(conversationId, response.chatMessages, response.hasMore);
    } catch (error) {
      this.showError(error);
    }
  }

  async searchMessages(conversationId: Guid, searchTerm: string): Promise<ChatMessageModel[]> {
    const facilityId = this.appStore.selectedFacilityId();

    if (!facilityId) {
      return [];
    }

    const scope = this.captureScope(facilityId);

    try {
      const response = await this.apiClient.get<GetChatMessagesResponse>(
        `${this.baseUrl(facilityId)}/conversations/${conversationId}/messages`,
        { take: SEARCH_PAGE_SIZE, searchTerm: searchTerm }
      );

      if (!this.acceptResponse(scope, response)) {
        return [];
      }

      const newestFirst = [...response.chatMessages].reverse();

      return newestFirst;
    } catch (error) {
      this.showError(error);

      return [];
    }
  }

  async getMessageReaders(messageId: Guid): Promise<ChatMessageReaderModel[]> {
    const facilityId = this.appStore.selectedFacilityId();

    if (!facilityId) {
      return [];
    }

    const scope = this.captureScope(facilityId);

    try {
      const response = await this.apiClient.get<GetChatMessageReadersResponse>(
        `${this.baseUrl(facilityId)}/messages/${messageId}/readers`
      );

      if (!this.acceptResponse(scope, response)) {
        return [];
      }

      return response.readers;
    } catch (error) {
      this.showError(error);

      return [];
    }
  }

  async markConversationRead(conversationId: Guid): Promise<void> {
    const facilityId = this.appStore.selectedFacilityId();
    const conversation = this.chatConversationStore.conversationById(conversationId);
    const loadedMessages = this.chatConversationStore.messagesFor(conversationId)();
    const newestStored = [...loadedMessages].reverse().find((message) => !message.isPending);
    const currentUserId = this.authStore.user()?.id;

    if (!facilityId || !conversation || !newestStored || !currentUserId) {
      return;
    }

    const scope = this.captureScope(facilityId);

    const member = ChatHelper.findActiveMember(conversation, currentUserId);

    if (!member) {
      return;
    }

    const lastReadDate = member.lastReadDate;
    const previousDateRead = member.dateRead;
    const isAlreadyRead =
      !!lastReadDate && new Date(newestStored.sentDate).getTime() <= new Date(lastReadDate).getTime();

    if (isAlreadyRead) {
      return;
    }

    this.chatConversationStore.patchMemberReadState(
      conversationId,
      currentUserId,
      newestStored.sentDate,
      new Date().toISOString()
    );

    try {
      const response = await this.apiClient.post<StoreChatLastReadRequest, StoreChatLastReadResponse>(
        `${this.baseUrl(facilityId)}/conversations/${conversationId}/read`,
        { lastReadMessageId: newestStored.id }
      );

      if (!this.acceptResponse(scope, response, true)) {
        this.chatConversationStore.patchMemberReadState(conversationId, currentUserId, lastReadDate, previousDateRead);
      }
    } catch {
      this.chatConversationStore.patchMemberReadState(conversationId, currentUserId, lastReadDate, previousDateRead);
    }
  }

  async loadUnreadFacilities(): Promise<void> {
    if (!this.isChatEnabled()) {
      return;
    }

    const scope = this.captureUserScope();

    try {
      const response = await this.apiClient.get<GetChatUnreadFacilitiesResponse>(UNREAD_URL);

      if (!this.acceptResponse(scope, response, true)) {
        return;
      }

      this.chatConversationStore.setUnreadFacilityIds(response.facilityIds);
    } catch {
      return;
    }
  }

  subscribeToChatEvents(): void {
    this.signalR.on<ChatMessageModel>(SignalREvents.ChatMessage, (message) => {
      this.handleChatEvent(() => void this.handleMessageBroadcast(message));
    });

    this.signalR.on<ChatMessageModel>(SignalREvents.ChatMessageDeleted, (message) => {
      this.handleChatEvent(() => this.applyMessageRevision(message));
    });

    this.signalR.on<unknown>(SignalREvents.ChatConversation, () => {
      this.handleChatEvent(() => void this.handleConversationBroadcast());
    });

    this.signalR.on<ChatReadModel>(SignalREvents.ChatRead, (read) => {
      this.handleChatEvent(() => this.handleReadBroadcast(read));
    });

    this.signalR.on<ChatReactionUpdateModel>(SignalREvents.ChatReaction, (update) => {
      this.handleChatEvent(() => this.handleReactionBroadcast(update));
    });
  }

  unsubscribeFromChatEvents(): void {
    this.signalR.off(SignalREvents.ChatMessage);
    this.signalR.off(SignalREvents.ChatMessageDeleted);
    this.signalR.off(SignalREvents.ChatConversation);
    this.signalR.off(SignalREvents.ChatRead);
    this.signalR.off(SignalREvents.ChatReaction);

    this.stopRefreshTimers();
  }

  async handleMessageBroadcast(message: ChatMessageModel): Promise<void> {
    const conversation = this.chatConversationStore.conversationById(message.conversationId);

    if (!conversation) {
      await this.refreshConversations();

      return;
    }

    this.chatConversationStore.upsertMessage(message.conversationId, message);

    const lastMessage = conversation.lastMessage;
    const isNewestMessage =
      !lastMessage
      || lastMessage.id === message.id
      || new Date(message.sentDate).getTime() >= new Date(lastMessage.sentDate).getTime();

    if (!isNewestMessage) {
      return;
    }

    this.chatConversationStore.patchConversation(message.conversationId, {
      lastMessage: message,
      lastActivityDate: message.sentDate,
    });
  }

  applyMessageRevision(message: ChatMessageModel): void {
    this.chatConversationStore.upsertMessage(message.conversationId, message);

    const conversation = this.chatConversationStore.conversationById(message.conversationId);

    if (conversation?.lastMessage?.id === message.id) {
      this.chatConversationStore.patchConversation(message.conversationId, { lastMessage: message });
    }
  }

  async handleConversationBroadcast(): Promise<void> {
    await this.refreshConversations();
  }

  handleReadBroadcast(read: ChatReadModel): void {
    const conversation = this.chatConversationStore.conversationById(read.conversationId);

    if (!conversation) {
      return;
    }

    const storedDate = conversation.members.find((member) => member.userId === read.userId)?.lastReadDate;

    if (storedDate && new Date(storedDate).getTime() >= new Date(read.lastReadDate).getTime()) {
      return;
    }

    this.chatConversationStore.patchMemberReadState(read.conversationId, read.userId, read.lastReadDate, read.dateRead);
  }

  handleReactionBroadcast(update: ChatReactionUpdateModel): void {
    this.chatConversationStore.patchMessageReaction(
      update.conversationId,
      update.messageId,
      update.userId,
      update.reaction
    );
  }

  startReadOnlyRefresh(conversationId: Guid): void {
    this.stopReadOnlyRefresh(conversationId);

    if (!this.isChatEnabled()) {
      return;
    }

    const handle = setInterval(() => {
      void this.refreshNewestMessages(conversationId);
    }, READ_ONLY_REFRESH_MS);

    this.readOnlyRefreshHandles.set(conversationId, handle);
  }

  stopReadOnlyRefresh(conversationId: Guid): void {
    const handle = this.readOnlyRefreshHandles.get(conversationId);

    if (handle === undefined) {
      return;
    }

    clearInterval(handle);
    this.readOnlyRefreshHandles.delete(conversationId);
  }

  startAllChatsRefresh(): void {
    this.stopAllChatsRefresh();

    if (!this.isChatEnabled() || !this.chatConversationStore.status()?.canViewAllConversations) {
      return;
    }

    this.allChatsRefreshHandle = setInterval(() => {
      void this.refreshConversations();
    }, ALL_CHATS_REFRESH_MS);
  }

  stopAllChatsRefresh(): void {
    if (this.allChatsRefreshHandle === null) {
      return;
    }

    clearInterval(this.allChatsRefreshHandle);
    this.allChatsRefreshHandle = null;
  }

  async sendMessage(
    conversationId: Guid,
    body: string,
    replyTo: ChatMessageModel | null = null
  ): Promise<ChatSendResult> {
    const trimmedBody = body.trim();
    const facilityId = this.appStore.selectedFacilityId();
    const currentUserId = this.authStore.user()?.id;

    if (!trimmedBody || !facilityId || !currentUserId) {
      return 'failed';
    }

    const scope = this.captureScope(facilityId);

    const pendingMessage = this.buildPendingMessage(conversationId, currentUserId, trimmedBody, replyTo);

    this.showPendingMessage(pendingMessage);

    const result = await this.enqueuePost(conversationId, () => this.postPendingMessage(scope, pendingMessage));

    return result;
  }

  async sendPhotoMessage(
    conversationId: Guid,
    file: File,
    replyTo: ChatMessageModel | null = null
  ): Promise<ChatSendResult> {
    const facilityId = this.appStore.selectedFacilityId();
    const currentUserId = this.authStore.user()?.id;

    if (!facilityId || !currentUserId || !isImageFile(file)) {
      return 'failed';
    }

    const scope = this.captureScope(facilityId);

    const preparedMessage = this.preparePhotoMessage(conversationId, currentUserId, file, replyTo);

    const result = await this.enqueuePost(conversationId, () => this.postPreparedPhoto(scope, preparedMessage));

    return result;
  }

  private async preparePhotoMessage(
    conversationId: Guid,
    currentUserId: Guid,
    file: File,
    replyTo: ChatMessageModel | null
  ): Promise<ChatMessageModel | null> {
    try {
      const photo = await preparePhoto(file);
      const pendingMessage = this.buildPendingMessage(conversationId, currentUserId, '', replyTo);
      const photoId = crypto.randomUUID();

      pendingMessage.photoId = photoId;
      pendingMessage.photoImageUrl = photo.imageDataUrl;
      pendingMessage.photoThumbnailUrl = photo.imageDataUrl;

      this.showPendingMessage(pendingMessage);

      const isUploaded = await this.uploadPhotoBlobs(photoId, photo);

      if (!isUploaded) {
        this.chatConversationStore.removeMessageByClientId(conversationId, pendingMessage.clientMessageId);

        return null;
      }

      return pendingMessage;
    } catch (error) {
      this.showError(error);

      return null;
    }
  }

  private async postPreparedPhoto(
    scope: RequestScope,
    preparedMessage: Promise<ChatMessageModel | null>
  ): Promise<ChatSendResult> {
    const pendingMessage = await preparedMessage;

    if (this.isStale(scope)) {
      this.removePendingPhoto(pendingMessage);

      return 'stale';
    }

    if (!pendingMessage) {
      return 'failed';
    }

    const result = await this.postPendingMessage(scope, pendingMessage);

    return result;
  }

  private removePendingPhoto(pendingMessage: ChatMessageModel | null): void {
    if (pendingMessage) {
      this.chatConversationStore.removeMessageByClientId(pendingMessage.conversationId, pendingMessage.clientMessageId);
    }
  }

  private enqueuePost(conversationId: Guid, post: () => Promise<ChatSendResult>): Promise<ChatSendResult> {
    const previous = this.postQueues.get(conversationId) ?? Promise.resolve<ChatSendResult>('sent');
    const queued = previous.then(post, post);
    const release = () => this.releasePostQueue(conversationId, queued);

    this.postQueues.set(conversationId, queued);
    void queued.then(release, release);

    return queued;
  }

  private releasePostQueue(conversationId: Guid, queued: Promise<ChatSendResult>): void {
    if (this.postQueues.get(conversationId) === queued) {
      this.postQueues.delete(conversationId);
    }
  }

  async editMessage(message: ChatMessageModel, body: string): Promise<boolean> {
    const facilityId = this.appStore.selectedFacilityId();

    if (!facilityId) {
      return false;
    }

    const scope = this.captureScope(facilityId);

    this.applyMessageRevision({ ...message, body: body, dateEdited: new Date().toISOString() });

    const request: StoreChatMessageRequest = {
      clientMessageId: message.clientMessageId,
      body: body,
      photoId: null,
      replyToMessageId: message.replyToMessageId,
    };

    try {
      const response = await this.apiClient.post<StoreChatMessageRequest, StoreChatMessageResponse>(
        `${this.baseUrl(facilityId)}/conversations/${message.conversationId}/messages`,
        request
      );

      if (!this.acceptResponse(scope, response)) {
        this.rollbackMessage(scope, message);

        return false;
      }

      this.applyMessageRevision(response.message);

      return true;
    } catch (error) {
      this.rollbackMessage(scope, message);
      this.showError(error);

      return false;
    }
  }

  async deleteMessage(message: ChatMessageModel): Promise<void> {
    const facilityId = this.appStore.selectedFacilityId();

    if (!facilityId) {
      return;
    }

    const scope = this.captureScope(facilityId);

    this.applyMessageRevision({
      ...message,
      body: null,
      photoId: null,
      photoImageUrl: null,
      photoThumbnailUrl: null,
      isDeleted: true,
    });

    try {
      const response = await this.apiClient.delete<DeleteChatMessageResponse>(
        `${this.baseUrl(facilityId)}/messages/${message.id}`
      );

      if (!this.acceptResponse(scope, response)) {
        this.rollbackMessage(scope, message);
      }
    } catch (error) {
      this.rollbackMessage(scope, message);
      this.showError(error);
    }
  }

  async reactToMessage(conversationId: Guid, messageId: Guid, reaction: string | null): Promise<void> {
    const facilityId = this.appStore.selectedFacilityId();
    const currentUserId = this.authStore.user()?.id;

    if (!facilityId || !currentUserId) {
      return;
    }

    const scope = this.captureScope(facilityId);

    const message = this.chatConversationStore
      .messagesFor(conversationId)()
      .find((candidate) => candidate.id === messageId);
    const previousReaction =
      message?.reactions.find((candidate) => candidate.userId === currentUserId)?.reaction ?? null;
    const nextReaction = reaction === previousReaction ? null : reaction;

    this.chatConversationStore.patchMessageReaction(conversationId, messageId, currentUserId, nextReaction);

    try {
      const response = await this.apiClient.post<StoreChatMessageReactionRequest, StoreChatMessageReactionResponse>(
        `${this.baseUrl(facilityId)}/messages/${messageId}/reaction`,
        { reaction: nextReaction }
      );

      if (!this.acceptResponse(scope, response)) {
        this.chatConversationStore.patchMessageReaction(conversationId, messageId, currentUserId, previousReaction);
      }
    } catch (error) {
      this.chatConversationStore.patchMessageReaction(conversationId, messageId, currentUserId, previousReaction);
      this.showError(error);
    }
  }

  async startGroupConversation(
    memberUserIds: Guid[],
    firstMessage: string
  ): Promise<ChatConversationModel | undefined> {
    const facilityId = this.appStore.selectedFacilityId();

    if (!facilityId) {
      return undefined;
    }

    const scope = this.captureScope(facilityId);

    const request: StoreChatConversationRequest = {
      conversationType: ChatConversationType.Group,
      name: null,
      memberUserIds: memberUserIds,
    };

    try {
      const response = await this.apiClient.post<StoreChatConversationRequest, StoreChatConversationResponse>(
        `${this.baseUrl(facilityId)}/conversations`,
        request
      );

      if (!this.acceptResponse(scope, response)) {
        return undefined;
      }

      this.chatConversationStore.upsertConversation(response.conversation);

      await this.sendMessage(response.conversation.id, firstMessage);

      return response.conversation;
    } catch (error) {
      this.showError(error);

      return undefined;
    }
  }

  async renameConversation(conversationId: Guid, name: string | null): Promise<void> {
    const facilityId = this.appStore.selectedFacilityId();

    if (!facilityId) {
      return;
    }

    const scope = this.captureScope(facilityId);

    const trimmedName = name?.trim() || null;

    this.chatConversationStore.patchConversation(conversationId, { name: trimmedName });

    await this.postAndReloadOnFailure<StoreChatConversationNameRequest, StoreChatConversationNameResponse>(
      scope,
      `${this.baseUrl(facilityId)}/conversations/${conversationId}/name`,
      { name: trimmedName }
    );
  }

  async setConversationPhoto(conversationId: Guid, file: File | null): Promise<void> {
    const facilityId = this.appStore.selectedFacilityId();

    if (!facilityId) {
      return;
    }

    const scope = this.captureScope(facilityId);

    if (file === null) {
      this.chatConversationStore.patchConversation(conversationId, {
        photoId: null,
        photoImageUrl: null,
        photoThumbnailUrl: null,
      });

      await this.postConversationPhoto(scope, conversationId, null);

      return;
    }

    try {
      const photo = await preparePhoto(file);
      const photoId = crypto.randomUUID();

      this.chatConversationStore.patchConversation(conversationId, {
        photoId: photoId,
        photoImageUrl: photo.imageDataUrl,
        photoThumbnailUrl: photo.imageDataUrl,
      });

      const isUploaded = await this.uploadPhotoBlobs(photoId, photo);

      if (!isUploaded) {
        await this.loadConversations();

        return;
      }

      await this.postConversationPhoto(scope, conversationId, photoId);
    } catch (error) {
      this.showError(error);
      await this.loadConversations();
    }
  }

  async addMembers(conversationId: Guid, userIds: Guid[]): Promise<void> {
    await this.storeMembers(conversationId, userIds, []);
  }

  async removeMembers(conversationId: Guid, userIds: Guid[]): Promise<ChatMembersResult> {
    const conversation = this.chatConversationStore.conversationById(conversationId);

    if (conversation) {
      const dateLeft = new Date().toISOString();

      this.chatConversationStore.upsertConversation({
        ...conversation,
        members: conversation.members.map((member) =>
          userIds.includes(member.userId) ? { ...member, dateLeft: dateLeft } : member
        ),
      });
    }

    return this.storeMembers(conversationId, [], userIds);
  }

  async leaveConversation(conversationId: Guid): Promise<void> {
    await this.removeSelfFromConversation(conversationId);
  }

  async deleteConversation(conversationId: Guid): Promise<void> {
    await this.removeSelfFromConversation(conversationId);
  }

  async setConversationMuted(conversationId: Guid, isMuted: boolean): Promise<void> {
    const facilityId = this.appStore.selectedFacilityId();
    const currentUserId = this.authStore.user()?.id;

    if (!facilityId || !currentUserId) {
      return;
    }

    const scope = this.captureScope(facilityId);

    const conversation = this.chatConversationStore.conversationById(conversationId);
    const previousDateMuted =
      conversation?.members.find((member) => member.userId === currentUserId)?.dateMuted ?? null;
    const dateMuted = isMuted ? new Date().toISOString() : null;

    this.chatConversationStore.patchMemberDateMuted(conversationId, currentUserId, dateMuted);

    const isStored = await this.postAndReloadOnFailure<StoreChatMutedRequest, StoreChatMutedResponse>(
      scope,
      `${this.baseUrl(facilityId)}/conversations/${conversationId}/muted`,
      { isMuted: isMuted }
    );

    if (!isStored) {
      this.chatConversationStore.patchMemberDateMuted(conversationId, currentUserId, previousDateMuted);
    }
  }

  async downloadPhoto(message: ChatMessageModel): Promise<void> {
    if (!message.photoImageUrl) {
      return;
    }

    try {
      const response = await fetch(message.photoImageUrl);

      if (!response.ok) {
        this.uiStore.showToast(PHOTO_DOWNLOAD_ERROR_MESSAGE, 'error');

        return;
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');

      anchor.href = objectUrl;
      anchor.download = `photo-${message.id}.jpg`;
      anchor.click();
      URL.revokeObjectURL(objectUrl);
    } catch {
      this.uiStore.showToast(PHOTO_DOWNLOAD_ERROR_MESSAGE, 'error');
    }
  }

  private async loadPeople(): Promise<void> {
    const facilityId = this.appStore.selectedFacilityId();

    if (!facilityId) {
      return;
    }

    const scope = this.captureScope(facilityId);

    const response = await this.apiClient.get<GetChatPeopleResponse>(`${this.baseUrl(facilityId)}/people`);

    if (!this.acceptResponse(scope, response)) {
      return;
    }

    this.chatConversationStore.setPeople(response.people);
  }

  private buildPendingMessage(
    conversationId: Guid,
    senderUserId: Guid,
    body: string,
    replyTo: ChatMessageModel | null
  ): ChatMessageModel {
    const clientMessageId = crypto.randomUUID();
    const replyPreview: ChatReplyPreviewModel | null = replyTo ? this.buildReplyPreview(replyTo) : null;

    return {
      id: clientMessageId,
      conversationId: conversationId,
      clientMessageId: clientMessageId,
      senderUserId: senderUserId,
      messageType: ChatMessageType.User,
      body: body,
      photoId: null,
      photoImageUrl: null,
      photoThumbnailUrl: null,
      replyToMessageId: replyTo?.id ?? null,
      replyTo: replyPreview,
      sentDate: new Date().toISOString(),
      isDeleted: false,
      dateEdited: null,
      reactions: [],
      isPending: true,
    };
  }

  private buildReplyPreview(replyTo: ChatMessageModel): ChatReplyPreviewModel {
    const previewBody = replyTo.isDeleted ? null : MentionHelper.parse(replyTo.body).body || null;

    return {
      messageId: replyTo.id,
      senderUserId: replyTo.senderUserId,
      body: previewBody,
      hasPhoto: !replyTo.isDeleted && !!replyTo.photoId,
      isDeleted: replyTo.isDeleted,
    };
  }

  private showPendingMessage(pendingMessage: ChatMessageModel): void {
    this.chatConversationStore.upsertMessage(pendingMessage.conversationId, pendingMessage);
    this.chatConversationStore.patchConversation(pendingMessage.conversationId, {
      lastMessage: pendingMessage,
      lastActivityDate: pendingMessage.sentDate,
    });
  }

  private async postPendingMessage(scope: RequestScope, pendingMessage: ChatMessageModel): Promise<ChatSendResult> {
    const conversationId = pendingMessage.conversationId;

    if (this.isStale(scope)) {
      this.chatConversationStore.removeMessageByClientId(conversationId, pendingMessage.clientMessageId);

      return 'stale';
    }

    const request: StoreChatMessageRequest = {
      clientMessageId: pendingMessage.clientMessageId,
      body: pendingMessage.body ?? '',
      photoId: pendingMessage.photoId,
      replyToMessageId: pendingMessage.replyToMessageId,
    };

    try {
      const response = await this.apiClient.post<StoreChatMessageRequest, StoreChatMessageResponse>(
        `${this.baseUrl(scope.facilityId)}/conversations/${conversationId}/messages`,
        request
      );

      if (this.isStale(scope)) {
        return 'stale';
      }

      if (!this.acceptResponse(scope, response)) {
        this.chatConversationStore.removeMessageByClientId(conversationId, pendingMessage.clientMessageId);

        return 'failed';
      }

      this.applyStoredMessage(response.message);

      return 'sent';
    } catch (error) {
      const result = this.handlePostFailure(scope, pendingMessage, error);

      return result;
    }
  }

  private handlePostFailure(scope: RequestScope, pendingMessage: ChatMessageModel, error: unknown): ChatSendResult {
    if (this.isStale(scope)) {
      return 'stale';
    }

    this.chatConversationStore.removeMessageByClientId(pendingMessage.conversationId, pendingMessage.clientMessageId);
    this.showError(error);

    return 'failed';
  }

  private applyStoredMessage(message: ChatMessageModel): void {
    this.chatConversationStore.upsertMessage(message.conversationId, message);
    this.chatConversationStore.patchConversation(message.conversationId, {
      lastMessage: message,
      lastActivityDate: message.sentDate,
    });

    const currentUserId = this.authStore.user()?.id;
    const conversation = this.chatConversationStore.conversationById(message.conversationId);
    const isActiveMember = !!conversation && ChatHelper.isActiveMember(conversation, currentUserId);

    if (currentUserId && isActiveMember) {
      this.chatConversationStore.patchMemberReadState(
        message.conversationId,
        currentUserId,
        message.sentDate,
        message.sentDate
      );
    }
  }

  private async uploadPhotoBlobs(photoId: Guid, photo: PreparedPhoto): Promise<boolean> {
    const presigned = await this.requestWriteUri(photoId);

    if (!presigned?.imageUri || !presigned.thumbnailUri) {
      this.uiStore.showToast(GENERIC_ERROR_MESSAGE, 'error');

      return false;
    }

    const isImageUploaded = await this.putBlob(presigned.imageUri, photo.imageBlob);
    const isThumbnailUploaded = isImageUploaded && (await this.putBlob(presigned.thumbnailUri, photo.thumbnailBlob));

    if (!isThumbnailUploaded) {
      this.uiStore.showToast(GENERIC_ERROR_MESSAGE, 'error');
    }

    return isThumbnailUploaded;
  }

  private async requestWriteUri(photoId: Guid): Promise<PresignedUri | null> {
    const request: GetPresignedUriRequest = { id: photoId, uriType: UriType.Write };

    try {
      return await this.apiClient.post<GetPresignedUriRequest, PresignedUri>(SAS_URL, request);
    } catch (error) {
      this.showError(error);

      return null;
    }
  }

  private async putBlob(uri: string, blob: Blob): Promise<boolean> {
    try {
      const response = await fetch(uri, {
        method: 'PUT',
        headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': 'image/jpg' },
        body: blob,
      });

      return response.ok;
    } catch {
      return false;
    }
  }

  private async postConversationPhoto(scope: RequestScope, conversationId: Guid, photoId: Guid | null): Promise<void> {
    try {
      const response = await this.apiClient.post<StoreChatConversationPhotoRequest, StoreChatConversationPhotoResponse>(
        `${this.baseUrl(scope.facilityId)}/conversations/${conversationId}/photo`,
        { photoId: photoId }
      );

      if (!this.acceptResponse(scope, response)) {
        await this.loadConversations();

        return;
      }

      this.chatConversationStore.upsertConversation(response.conversation);
    } catch (error) {
      this.showError(error);
      await this.loadConversations();
    }
  }

  private async storeMembers(
    conversationId: Guid,
    addUserIds: Guid[],
    removeUserIds: Guid[]
  ): Promise<ChatMembersResult> {
    const facilityId = this.appStore.selectedFacilityId();

    if (!facilityId) {
      return 'failed';
    }

    const scope = this.captureScope(facilityId);

    const request: StoreChatMembersRequest = { addUserIds: addUserIds, removeUserIds: removeUserIds };

    try {
      const response = await this.apiClient.post<StoreChatMembersRequest, StoreChatMembersResponse>(
        `${this.baseUrl(facilityId)}/conversations/${conversationId}/members`,
        request
      );

      return await this.applyMembersResponse(scope, conversationId, response);
    } catch (error) {
      this.showError(error);
      await this.loadConversations();

      return 'failed';
    }
  }

  private async applyMembersResponse(
    scope: RequestScope,
    conversationId: Guid,
    response: StoreChatMembersResponse
  ): Promise<ChatMembersResult> {
    if (!this.acceptResponse(scope, response)) {
      await this.loadConversations();

      return 'failed';
    }

    if (response.conversationDeleted || !response.conversation) {
      this.chatDockStore.closeWindow(conversationId);
      this.chatConversationStore.removeConversation(conversationId);

      return 'deleted';
    }

    this.chatConversationStore.upsertConversation(response.conversation);

    return 'stored';
  }

  private async removeSelfFromConversation(conversationId: Guid): Promise<void> {
    const facilityId = this.appStore.selectedFacilityId();
    const currentUserId = this.authStore.user()?.id;

    if (!facilityId || !currentUserId) {
      return;
    }

    const scope = this.captureScope(facilityId);

    this.chatDockStore.closeWindow(conversationId);
    this.chatConversationStore.removeConversation(conversationId);

    await this.postAndReloadOnFailure<StoreChatMembersRequest, StoreChatMembersResponse>(
      scope,
      `${this.baseUrl(facilityId)}/conversations/${conversationId}/members`,
      { addUserIds: [], removeUserIds: [currentUserId] }
    );
  }

  private async postAndReloadOnFailure<TRequest, TResponse extends ApiResponse>(
    scope: RequestScope,
    url: string,
    request: TRequest
  ): Promise<boolean> {
    try {
      const response = await this.apiClient.post<TRequest, TResponse>(url, request);
      const isAccepted = this.acceptResponse(scope, response);

      if (!isAccepted) {
        await this.loadConversations();
      }

      return isAccepted;
    } catch (error) {
      this.showError(error);
      await this.loadConversations();

      return false;
    }
  }

  private handleSessionChange(facilityId: Guid | null, userId: Guid | null): void {
    if (facilityId === this.lastObservedFacilityId && userId === this.lastObservedUserId) {
      return;
    }

    this.lastObservedFacilityId = facilityId;
    this.lastObservedUserId = userId;

    this.stopRefreshTimers();
    this.postQueues.clear();
    this.chatDockStore.clearForFacilityChange();
    this.chatConversationStore.clear();

    if (userId === null) {
      this.chatDockStore.removeAllPersisted();
      this.chatConversationStore.setManageableFacilityIds([]);
    }

    this.chatDockStore.restoreForFacility(facilityId);

    if (userId === null) {
      return;
    }

    void this.loadForFacility();
  }

  private handleConnectionChange(isConnected: boolean): void {
    const isReconnect = isConnected && !this.wasSignalRConnected && this.hasSignalRConnected;

    this.wasSignalRConnected = isConnected;
    this.hasSignalRConnected = this.hasSignalRConnected || isConnected;

    if (!isReconnect || !this.isChatEnabled()) {
      return;
    }

    void this.refreshConversations();
    void this.loadUnreadFacilities();

    for (const window of this.chatDockStore.windows()) {
      void this.refreshNewestMessages(window.conversationId);
    }
  }

  private stopRefreshTimers(): void {
    this.clearUnreadDebounce();
    this.stopAllChatsRefresh();

    for (const conversationId of this.readOnlyRefreshHandles.keys()) {
      this.stopReadOnlyRefresh(conversationId);
    }
  }

  private handleChatEvent(applyEvent: () => void): void {
    if (!this.isChatEnabled()) {
      return;
    }

    applyEvent();
    this.scheduleUnreadRefresh();
  }

  private isChatEnabled(): boolean {
    const isEnabled = this.chatConversationStore.status()?.isEnabled === true;

    return isEnabled;
  }

  private scheduleUnreadRefresh(): void {
    this.clearUnreadDebounce();

    if (!this.isChatEnabled()) {
      return;
    }

    this.unreadDebounceHandle = setTimeout(() => {
      this.unreadDebounceHandle = null;
      void this.loadUnreadFacilities();
    }, UNREAD_DEBOUNCE_MS);
  }

  private clearUnreadDebounce(): void {
    if (this.unreadDebounceHandle === null) {
      return;
    }

    clearTimeout(this.unreadDebounceHandle);
    this.unreadDebounceHandle = null;
  }

  private rollbackMessage(scope: RequestScope, message: ChatMessageModel): void {
    if (this.isStale(scope)) {
      return;
    }

    this.applyMessageRevision(message);
  }

  private captureUserScope(): UserScope {
    const userId = this.authStore.user()?.id ?? null;

    return { userId: userId };
  }

  private captureScope(facilityId: Guid): RequestScope {
    const userId = this.authStore.user()?.id ?? null;

    return { facilityId: facilityId, userId: userId };
  }

  private isStale(scope: UserScope | RequestScope): boolean {
    const currentUserId = this.authStore.user()?.id ?? null;

    if (scope.userId !== currentUserId) {
      return true;
    }

    if (!('facilityId' in scope)) {
      return false;
    }

    const currentFacilityId = this.appStore.selectedFacilityId();

    return scope.facilityId !== currentFacilityId;
  }

  private baseUrl(facilityId: Guid): string {
    return `admin/chat/facilities/${facilityId}`;
  }

  private acceptResponse(
    scope: UserScope | RequestScope,
    response: ApiResponse | undefined,
    isQuiet = false
  ): response is ApiResponse {
    if (this.isStale(scope)) {
      return false;
    }

    if (response?.success) {
      return true;
    }

    if (isQuiet) {
      return false;
    }

    this.uiStore.showToast(response?.messages?.[0] ?? GENERIC_ERROR_MESSAGE, 'error');

    return false;
  }

  private showErrorUnlessQuiet(error: unknown, isQuiet: boolean): void {
    if (isQuiet) {
      return;
    }

    this.showError(error);
  }

  private showError(error: unknown): void {
    const serverMessage = error instanceof HttpErrorResponse ? error.error?.messages?.[0] : undefined;
    const message = serverMessage ?? GENERIC_ERROR_MESSAGE;

    this.uiStore.showToast(message, 'error');
  }
}
